/**
 * @unscoped — i18n 공유 base — Cycle 41.
 *
 * admin-i18n.ts + drift-i18n.ts 의 공통 state (lang / storage / listener) 통합.
 * 두 모듈이 자체 lang 변수 / Set 유지하던 구조 → 단일 globalThis 객체 공유.
 *
 * 정책:
 *   - 단일 lang state (globalThis.__i18n) — admin/drift 가 항상 동일 값
 *   - 단일 storage key 'workchat:admin-lang' (cycle 38 의 admin-i18n 기준 유지 — 마이그레이션 비용 회피)
 *   - 단일 listener Set — setSharedLang 호출 시 양쪽 module 모두 통지
 *   - admin-i18n/drift-i18n 의 기존 public API (setAdminLang/setDriftLang) 는 thin wrapper 로 호환
 *
 * vitest ESM dual-instance 회피 — globalThis 기반 ensureState (cycle 39 패턴 일관).
 */

// Cycle 45 — third lang 'ja' (일본어) 추가.
export type Lang = 'ko' | 'en' | 'ja';

export const FALLBACK_LANG: Lang = 'ko';
export const SHARED_LANG_STORAGE_KEY = 'workchat:admin-lang';

export function isLang(v: unknown): v is Lang {
  return v === 'ko' || v === 'en' || v === 'ja';
}

interface I18nState { lang: Lang; listeners: Set<() => void> }
const G = globalThis as unknown as { __i18n?: I18nState };

function detectLangInitial(): Lang {
  if (typeof localStorage !== 'undefined') {
    try {
      const stored = localStorage.getItem(SHARED_LANG_STORAGE_KEY);
      if (isLang(stored)) return stored;
    } catch { /* private mode 무시 */ }
  }
  // 한국 대상 앱 — 명시 선택(localStorage)이 없으면 항상 한글 디폴트.
  // 이전엔 navigator.language 를 자동 감지해 영어/일본어 브라우저에서 로그인 화면부터
  // 그 언어로 표시됐다(한글 디폴트 위반). 영어/일본어는 설정 화면에서 명시 선택 시
  // localStorage 에 영속되고, 로그인 후에는 AuthGuard 가 서버 settings.language 를 적용한다.
  // navigator 기반 자동 감지가 필요한 경우 사용자가 AdminConsole 의 'auto' 옵션(setAutoLang)을 명시 선택.
  return FALLBACK_LANG; // 'ko'
}

function ensureState(): I18nState {
  if (!G.__i18n) {
    G.__i18n = { lang: detectLangInitial(), listeners: new Set() };
  }
  return G.__i18n!;
}

export function getSharedLang(): Lang {
  return ensureState().lang;
}

/**
 * Cycle 42 — lang → Intl locale BCP 47 tag.
 *   ko → ko-KR, en → en-US.
 *
 * date/number 형식 (toLocaleString, Intl.DateTimeFormat 등) 의 인자로 사용.
 * 향후 lang 추가 (ja → ja-JP, zh → zh-CN) 시 본 helper 단일 진입점.
 */
export function getDateLocale(): string {
  // Cycle 45 — ja 추가
  switch (getSharedLang()) {
    case 'en': return 'en-US';
    case 'ja': return 'ja-JP';
    default:   return 'ko-KR';
  }
}

/**
 * Cycle 43 — Number.toLocaleString 의 lang-aware locale.
 *   현재는 getDateLocale 와 동일 (ko-KR/en-US) — 동일 region 가정.
 *   향후 number-only locale 차이 발생 시 분리 (예: ko-KR-x-thousand) — 본 helper 가 분기 진입점.
 */
export function getNumberLocale(): string {
  return getDateLocale();
}

/**
 * Cycle 45 — Intl.NumberFormat currency formatter.
 *   lang-aware locale + currency code 결합. 기본 KRW (한국 원화).
 *   ko-KR + KRW → '₩1,234', en-US + KRW → '₩1,234' / 'KRW 1,234' (browser 마다 다름), ja-JP + KRW → '₩1,234'
 *
 *   결과 caching 없음 — locale/currency 가 적은 caller 라 매번 새 Intl.NumberFormat 생성 OK.
 *   향후 hot path 시 cache 검토.
 */
export function getCurrencyFormatter(currency: string = 'KRW'): Intl.NumberFormat {
  return new Intl.NumberFormat(getDateLocale(), { style: 'currency', currency });
}

/**
 * Cycle 54 — lang-aware 날짜/시각 포맷 SSOT.
 *
 * 배경: 컴포넌트 ~20곳이 `new Date(iso).toLocaleString(...)` 을 inline 처리하며 로케일이
 *   불일치 — 일부 getDateLocale(), 일부 하드코딩 'ko-KR', 일부 로케일 없는 bare default.
 *   → ja/en 사용자가 날짜를 한국어/브라우저기본으로 보는 i18n 버그 (cycle 45-47 ja 롤아웃 사각지대).
 *   추가로 invalid/null 입력 시 "Invalid Date" 노출 (NaN 가드 불일치).
 *
 * 본 SSOT 가 getDateLocale() 강제 + invalid/null → fallback 통일.
 *   - formatDate: 날짜만 (toLocaleDateString)
 *   - formatDateTime: 날짜+시각 (toLocaleString)
 *   - formatTime: 시각만 2-digit (toLocaleTimeString, hour/minute)
 */
function _parseDate(iso: string | number | Date | null | undefined): Date | null {
  if (iso == null || iso === '') return null;
  const d = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(iso: string | number | Date | null | undefined, fallback = ''): string {
  const d = _parseDate(iso);
  return d == null ? fallback : d.toLocaleDateString(getDateLocale());
}

/**
 * ⛔⛔ `toLocaleString()` 의 **기본 옵션을 «글자로» 적은 것** (2026-10-06).
 *
 * ## 왜 필요한가 — `formatToParts` 는 «기본값»을 모른다
 *
 * ```
 * d.toLocaleString('ko-KR')                    ⇒ 「2026. 1. 5. 오전 12:00:00」
 * new Intl.DateTimeFormat('ko-KR').format(d)   ⇒ ⛔ 「2026. 1. 5.」   ← ★ «날짜만»이다!
 * ```
 * ⇒ ★ 즉 옵션 없이 `Intl.DateTimeFormat` 을 쓰면 ⭐ **시각이 통째로 사라진다.**
 *   ⇒ ⇒ 그래서 자정 고침을 붙이려면 **그 기본값을 적어 줘야** 한다.
 *
 * ## ✅ 같은 글자가 나오는지 ★ 재서 확인했다
 *
 * `ko-KR`·`en-US`·`ja-JP` × 다섯 시각(0:00:00 · 8:05:09 · 12:00:00 · 13:45:30 · 23:59:59)에서
 * `toLocaleString(loc)` 와 `Intl.DateTimeFormat(loc, 이 옵션).format()` 이 ⭐ **한 글자도 안 다르다**.
 * ⚠ 그래도 **ICU 판이 다르면 갈릴 수 있다** — 그래서 시험이 「날짜·초가 그대로 있나」를 함께 본다.
 */
const _DATETIME_DEFAULT_OPTS: Intl.DateTimeFormatOptions = {
  year: 'numeric', month: 'numeric', day: 'numeric',
  hour: 'numeric', minute: 'numeric', second: 'numeric',
};

/**
 * 날짜＋시각 — ⭐ **한국어 자정이 「오전 0시」다**(2026-10-06 사용자 결정 「네」 · 2차).
 *
 * ⇒ `formatTime` 과 **같은 규칙**(`_koZeroHour`)을 쓴다 — ⛔ 두 함수가 다른 말을 하면
 *   같은 화면에서 「오전 0:00」과 「오전 12:00:00」이 **나란히** 보인다.
 * ⚠ 정오(「오후 12:00:00」)는 **그대로**다 — 결정이 **자정 한 가지**였다(`formatTime` 머리말).
 */
export function formatDateTime(iso: string | number | Date | null | undefined, fallback = ''): string {
  return formatDateTimeWith(iso, _DATETIME_DEFAULT_OPTS, fallback);
}

/**
 * 날짜＋시각을 **제 옵션으로** — ⭐ 그래도 **한국어 자정 규칙은 그대로** (2026-10-06 · 판 ⓑ).
 *
 * ## ⛔ 왜 `formatDateTime` 으로 «갈아탈 수 없는» 자리가 있나
 *
 * 화면마다 **보이는 꼴이 다르다.** 예를 들어 워크스페이스 셋은 `2-digit` 월·일에 **초가 없다**:
 * ```
 * 그 옵션   { year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit' }
 *   ⇒ 「2026. 01. 05. 오후 02:03」
 * formatDateTime ⇒ 「2026. 1. 5. 오후 2:03:07」   ← ⛔ 월·일이 한 자리가 되고 ★ «초»가 생긴다
 * ```
 * ⇒ ★ 그러니 그 자리를 `formatDateTime` 으로 바꾸면 **「자정 고침」이 아니라 «포맷 변경»**이다.
 *   ⇒ ⭐ 그 화면들은 사용자가 **기록을 견주는** 곳이라 ⛔ 함부로 바꾸면 **옛 캡처와 안 맞는다.**
 * ⇒ ⇒ ★ 그래서 **옵션은 그대로 두고** 자정 규칙만 입히는 길을 둔다.
 *
 * ⚠ 새 자리에서는 **되도록 `formatDateTime` 을 쓴다** — 이 함수는 **이미 제 꼴이 있는** 화면을
 *   옮겨 올 때의 것이다. 옵션을 **새로** 지어내면 그만큼 꼴이 또 갈라진다.
 */
export function formatDateTimeWith(
  iso: string | number | Date | null | undefined,
  opts: Intl.DateTimeFormatOptions,
  fallback = '',
): string {
  const d = _parseDate(iso);
  if (d == null) return fallback;
  const locale = getDateLocale();
  return _koZeroHour(d, locale, opts) ?? d.toLocaleString(locale, opts);
}

/**
 * ⭐⭐ **한국어 자정을 「오전 12:00」이 아니라 「오전 0:00」으로** (2026-10-06 사용자 결정 「네」).
 *
 * ## 무엇이 문제였나
 *
 * 운영 보고에서 일정 199 가 **「오전 12:00 ~ 오전 08:00」**으로 보였다. 12시간제 자정 표기라
 * **결함은 아니지만** 한국어에서 자정은 보통 **「오전 0시」**라 하고 「오전 12시」는 **낮 12시와
 * 헷갈린다**. ⇒ 사용자가 바꾸라고 했다.
 *
 * ## ⛔ 왜 ko «만» 인가 — 재서 골랐다
 *
 * ```
 * 실측(2026-10-06 · Intl · { hour: '2-digit', minute: '2-digit' }):
 *   ko-KR   자정 ⇒ 「오전 12:00」   정오 ⇒ 「오후 12:00」   ← ★ 이것만 고친다
 *   en-US   자정 ⇒ 「12:00 AM」     정오 ⇒ 「12:00 PM」     ← ⛔ 영어의 «표준»이다 — 바꾸면 더 낯설다
 *   ja-JP   자정 ⇒ 「00:00」        정오 ⇒ 「12:00」        ← ⭐ 24시간제라 ★ «무관»하다
 * ```
 * ⇒ ★ 그래서 **한국어에서만** 손댄다. ⛔ en 을 건드리면 **고치는 것이 아니라 깨는 것**이다.
 *
 * ## ⚠ 「오후 12:00」(정오)는 ★ **그대로 둔다**
 *
 * 자정만 바꾸는 것이 **비대칭으로 보이지만** 한국어 관행이 그렇다 — 자정은 「오전 0시」,
 * 정오는 「오후 12시」·「낮 12시」로 말한다. ⇒ 정오를 「오후 0:00」으로 바꾸면 **덜** 익숙하다.
 * ⇒ ⛔ **다만 이것은 내 판단이고 «사용자 결정은 자정 한 가지»였다** — 정오도 바꿀지는 따로 물었다.
 *
 * ## ★ 왜 글자를 바꾸지 않고 `formatToParts` 인가
 *
 * 포맷 결과를 정규식으로 고치면 **로케일이 바뀔 때 조용히 틀린다**(자리·구분자·「오전」의 위치가
 * 다르다). ⇒ `formatToParts` 로 **`hour` 조각만** 갈아 끼우면 나머지는 **Intl 이 그대로** 만든다.
 */
function _koZeroHour(d: Date, locale: string, opts: Intl.DateTimeFormatOptions): string | null {
  // ⛔ 자정이 «아니면» 손대지 않는다 · ⛔ 한국어가 아니면 손대지 않는다.
  if (d.getHours() !== 0 || !locale.startsWith('ko')) return null;
  return new Intl.DateTimeFormat(locale, opts)
    .formatToParts(d)
    .map((p) => (p.type === 'hour' ? '0' : p.value))
    .join('');
}

export function formatTime(iso: string | number | Date | null | undefined, fallback = ''): string {
  const d = _parseDate(iso);
  if (d == null) return fallback;
  const locale = getDateLocale();
  const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  return _koZeroHour(d, locale, opts) ?? d.toLocaleTimeString(locale, opts);
}

/**
 * Cycle 44 — 사용자가 'auto' 선택 시 호출.
 *   localStorage 의 lang 명시 선택 제거 → 다음 detectLang 호출 시 navigator.language fallback.
 *   현재 lang state 도 즉시 navigator-detect 결과로 update + listener 통지.
 */
export function setAutoLang(): void {
  if (typeof localStorage !== 'undefined') {
    try { localStorage.removeItem(SHARED_LANG_STORAGE_KEY); } catch { /* */ }
  }
  // navigator detect — localStorage 빈 상태에서 detectLangInitial 동등 동작
  let detected: Lang = FALLBACK_LANG;
  if (typeof navigator !== 'undefined') {
    const raw = (navigator.language || navigator.languages?.[0] || '').toLowerCase();
    // Cycle 45 — ja 추가 (detectLangInitial 와 일관)
    if (raw.startsWith('ja')) detected = 'ja';
    else if (raw.startsWith('en')) detected = 'en';
  }
  const s = ensureState();
  s.lang = detected;
  // idempotent — same-value 도 listener 통지 (cycle 41 정책 일관)
  for (const l of s.listeners) l();
}

/**
 * Cycle 44 — 사용자가 명시 lang 선택했는지 (localStorage 에 저장 있음).
 *   false → 'auto' 모드 (navigator detect).
 */
export function hasExplicitLang(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return isLang(localStorage.getItem(SHARED_LANG_STORAGE_KEY));
  } catch { return false; }
}

/**
 * 단일 진입점 — admin/drift 양쪽 모두 동일 lang 으로 통일 + listeners 통지.
 * - localStorage 영속 (write 실패는 silent — private mode 호환)
 * - 같은 값이어도 listener 통지 (cycle 39 의 cross-module early-return trap 회피 patch)
 */
export function setSharedLang(value: Lang): void {
  if (!isLang(value)) return;
  const s = ensureState();
  const changed = s.lang !== value;
  if (changed) {
    s.lang = value;
    if (typeof localStorage !== 'undefined') {
      try { localStorage.setItem(SHARED_LANG_STORAGE_KEY, value); } catch { /* */ }
    }
  }
  // 통지 — admin/drift 양쪽 React component 모두 re-render.
  // 같은 값이어도 통지 (idempotent — listener 가 결과 갱신 보장)
  for (const l of s.listeners) l();
}

export function subscribeSharedLang(fn: () => void): () => void {
  const s = ensureState();
  s.listeners.add(fn);
  return () => { s.listeners.delete(fn); };
}

/**
 * 서버 settings.language 적용 — 부트(로그인 직후) sync 전용.
 *
 * setSharedLang 과 달리 localStorage 에 저장하지 않는다: 서버 settings 가 사용자 계정 언어의
 * SSOT 이므로 부트(AuthGuard probe 직후)마다 서버값을 i18n state 에만 반영한다 — 다른 기기에서
 * 변경해도 이 기기 부트에 즉시 반영되고, stale localStorage 값에 가려지지 않는다.
 *
 * 봉합: 기존엔 부트 시 서버 settings.language 를 i18n 에 잇는 코드가 없어, localStorage 미설정
 * 사용자는 navigator.language(브라우저 언어)로 fallback — 설정이 한국어여도 영어 브라우저면
 * 전체 UI 가 영어로 표시됐다.
 */
export function applyServerLang(value: Lang): void {
  if (!isLang(value)) return;
  const s = ensureState();
  if (s.lang === value) return;
  s.lang = value;
  for (const l of s.listeners) l();
}

/** test 격리용 — production 호출 금지. */
export function _setSharedLangForTests(value: Lang): void {
  const s = ensureState();
  s.lang = value;
  for (const l of s.listeners) l();
}

/** test 진단용. */
export function _getSharedLangForTests(): Lang {
  return ensureState().lang;
}
